import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2, Plus, X, Calendar, Lock, Unlock, Trash2, AlertTriangle, CheckCircle2, CheckSquare, Square, MoreVertical } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (base, n) => {
  const d = new Date(base + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const WEEKDAY_OPTIONS = [
  { idx: 0, short: "Mon" },
  { idx: 1, short: "Tue" },
  { idx: 2, short: "Wed" },
  { idx: 3, short: "Thu" },
  { idx: 4, short: "Fri" },
  { idx: 5, short: "Sat" },
  { idx: 6, short: "Sun" },
];

function StatusBadge({ status }) {
  const map = {
    open:    { label: "Open",    cls: "bg-[#B8C9BD]/25 text-[#4F6B55]",   Icon: CheckCircle2 },
    blocked: { label: "Blocked", cls: "bg-[#E8DED0] text-[#8B6B3F]",       Icon: Lock },
    booked:  { label: "Booked",  cls: "bg-[#C9A0A0]/25 text-[#8B4F4F]",    Icon: Calendar },
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-700",       Icon: Loader2 },
  };
  const m = map[status] || { label: status, cls: "bg-[#282828]/10 text-[#282828]", Icon: Calendar };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${m.cls}`} data-testid={`slot-status-${status}`}>
      <Icon className="w-3 h-3" strokeWidth={2} />
      {m.label}
    </span>
  );
}

export default function AdminTrialSlots() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(addDaysISO(todayISO(), 30));

  // Create form — two tabs
  const [createMode, setCreateMode] = useState("quick"); // "quick" | "range"

  // Quick form state
  const [qDate, setQDate] = useState(todayISO());
  const [qTimes, setQTimes] = useState(["10:00"]);
  const [qRepeat, setQRepeat] = useState(1);

  // Range form state
  const [rFrom, setRFrom] = useState(todayISO());
  const [rTo, setRTo] = useState(addDaysISO(todayISO(), 6));
  const [rWeekdays, setRWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]); // Mon..Sun
  const [rStart, setRStart] = useState("15:00");
  const [rEnd, setREnd] = useState("22:00");

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/booking/admin/trial-slots`, {
        params: { from_date: fromDate, to_date: toDate },
        headers: headers(),
      });
      setSlots(res.data.slots || []);
      setSelectedIds(new Set()); // clear selection on refresh
    } catch {
      toast.error("Failed to load trial slots");
    }
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  // ── Quick create ──
  const addTimeInput = () => setQTimes(t => [...t, ""]);
  const updateTimeInput = (idx, val) => setQTimes(t => t.map((v, i) => i === idx ? val : v));
  const removeTimeInput = (idx) => setQTimes(t => t.length === 1 ? t : t.filter((_, i) => i !== idx));

  const handleQuickCreate = async (e) => {
    e.preventDefault();
    const times = qTimes.map(t => (t || "").trim()).filter(Boolean);
    if (!qDate || times.length === 0) { toast.error("Pick a date and at least one start time"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/booking/admin/trial-slots`, {
        date: qDate, start_times: times, repeat_daily_days: Math.max(1, parseInt(qRepeat) || 1), status: "open",
      }, { headers: headers() });
      const c = (res.data.created || []).length;
      const s = (res.data.skipped || []).length;
      if (c > 0) toast.success(`Opened ${c} slot(s)${s ? ` · ${s} skipped` : ""}`);
      else if (s > 0) toast.warning(`No new slots — ${s} skipped (duplicates or invalid times)`);
      setQTimes(["10:00"]); setQRepeat(1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open slots");
    }
    setSaving(false);
  };

  // ── Range generate ──
  const toggleWeekday = (idx) => {
    setRWeekdays(w => w.includes(idx) ? w.filter(x => x !== idx) : [...w, idx].sort());
  };

  const handleRangeGenerate = async (e) => {
    e.preventDefault();
    if (rWeekdays.length === 0) { toast.error("Select at least one weekday"); return; }
    if (!rFrom || !rTo || !rStart || !rEnd) { toast.error("Fill all range fields"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/booking/admin/trial-slots/bulk-generate`, {
        from_date: rFrom, to_date: rTo, weekdays: rWeekdays,
        window_start: rStart, window_end: rEnd, status: "open",
      }, { headers: headers() });
      const c = res.data.created_count || 0;
      const s = res.data.skipped_count || 0;
      if (c > 0) toast.success(`Generated ${c} slots (${res.data.duration_minutes}m each)${s ? ` · ${s} skipped as duplicates` : ""}`);
      else if (s > 0) toast.warning(`No new slots — ${s} duplicates skipped`);
      else toast.warning("No matching weekdays in the selected range");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate slots");
    }
    setSaving(false);
  };

  // ── Single row actions ──
  const toggleBlock = async (slot) => {
    if (slot.status === "booked") return;
    const nextStatus = slot.status === "blocked" ? "open" : "blocked";
    try {
      await axios.patch(`${API}/booking/admin/trial-slots/${slot.id}`, { status: nextStatus }, { headers: headers() });
      toast.success(`Slot ${nextStatus === "blocked" ? "blocked" : "reopened"}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update slot");
    }
  };
  const deleteSlot = async (slot) => {
    if (slot.status === "booked") { toast.error("Booked slots must be cancelled via the trial appointment."); return; }
    if (!window.confirm(`Delete slot ${slot.date} ${slot.start_time}?`)) return;
    try {
      await axios.delete(`${API}/booking/admin/trial-slots/${slot.id}`, { headers: headers() });
      toast.success("Slot deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete slot");
    }
  };

  // ── Selection helpers ──
  const eligibleSlots = useMemo(() => slots.filter(s => s.status !== "booked"), [slots]);
  const eligibleIds = useMemo(() => new Set(eligibleSlots.map(s => s.id)), [eligibleSlots]);
  const allVisibleSelected = eligibleIds.size > 0 && [...eligibleIds].every(id => selectedIds.has(id));

  const toggleSelect = (slot) => {
    if (slot.status === "booked") return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(slot.id)) next.delete(slot.id); else next.add(slot.id);
      return next;
    });
  };
  const selectAllVisible = () => {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleIds));
  };
  const selectDay = (dateStr) => {
    const daySlots = slots.filter(s => s.date === dateStr && s.status !== "booked");
    const dayIds = daySlots.map(s => s.id);
    const allSelected = dayIds.length > 0 && dayIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) dayIds.forEach(id => next.delete(id));
      else dayIds.forEach(id => next.add(id));
      return next;
    });
  };

  const runBulk = async (action) => {
    if (selectedIds.size === 0) { toast.error("Select at least one slot"); return; }
    if (action === "delete" && !window.confirm(`Delete ${selectedIds.size} selected slot(s)? This cannot be undone.`)) return;
    setBulkSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking/admin/trial-slots/bulk-action`, {
        slot_ids: [...selectedIds], action,
      }, { headers: headers() });
      const n = res.data.updated_count || 0;
      const sk = (res.data.skipped || []).length;
      const verb = action === "block" ? "blocked" : action === "unblock" ? "reopened" : "deleted";
      if (n > 0) toast.success(`${n} slot(s) ${verb}${sk ? ` · ${sk} skipped` : ""}`);
      else toast.warning(`No slots ${verb}${sk ? ` — ${sk} skipped` : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
    setBulkSubmitting(false);
  };

  // Group slots by date for display
  const grouped = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const dateKeys = Object.keys(grouped).sort();

  return (
    <AdminLayout
      title="Trial Availability"
      subtitle="Open, block, or delete trial slots offered on the direct booking flow"
    >
      {/* Info banner */}
      <div className="bg-[#FAF8F0] border border-[#282828]/10 p-4 mb-5 flex items-start gap-3" data-testid="trial-slots-info">
        <AlertTriangle className="w-4 h-4 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-[12px] text-[#676664] leading-relaxed">
          Slots inherit the current <strong>Trial duration</strong> from Booking Types settings.
          Customers only see <strong>Open</strong> slots that don&apos;t conflict with existing appointments.
          Booked slots must be cancelled or rescheduled via the Trial appointment record.
        </div>
      </div>

      {/* Create form with tabs */}
      <div id="create-slots-form" className="bg-white border border-[#282828]/8 mb-6" data-testid="create-slots-form">
        <div className="flex border-b border-[#282828]/8">
          <button
            type="button"
            onClick={() => setCreateMode("quick")}
            className={`px-5 py-3 text-[12px] tracking-wide ${createMode === "quick" ? "text-[#9A7B4F] border-b-2 border-[#9A7B4F] -mb-px" : "text-[#676664]/60 hover:text-[#282828]"}`}
            data-testid="create-mode-quick"
          >
            Quick add
          </button>
          <button
            type="button"
            onClick={() => setCreateMode("range")}
            className={`px-5 py-3 text-[12px] tracking-wide ${createMode === "range" ? "text-[#9A7B4F] border-b-2 border-[#9A7B4F] -mb-px" : "text-[#676664]/60 hover:text-[#282828]"}`}
            data-testid="create-mode-range"
          >
            Generate from window
          </button>
        </div>

        {createMode === "quick" ? (
          <form onSubmit={handleQuickCreate} className="p-5 space-y-4" data-testid="quick-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Date</label>
                <input type="date" required min={todayISO()} value={qDate} onChange={e => setQDate(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="quick-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Repeat for N days (from date)</label>
                <input type="number" min={1} max={60} value={qRepeat} onChange={e => setQRepeat(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="quick-repeat" />
                <p className="text-[10px] text-[#676664]/40 mt-1">1 = only that date.</p>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white w-full" data-testid="quick-submit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open Slots"}
                </Button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-2">Start times</label>
              <div className="flex flex-wrap gap-2">
                {qTimes.map((t, i) => (
                  <div key={i} className="flex items-center border border-[#282828]/12 bg-white" data-testid={`quick-time-row-${i}`}>
                    <input type="time" value={t} onChange={e => updateTimeInput(i, e.target.value)}
                      className="px-2 py-2 text-xs w-[110px] focus:outline-none" data-testid={`quick-time-input-${i}`} />
                    {qTimes.length > 1 && (
                      <button type="button" onClick={() => removeTimeInput(i)} className="px-1 text-[#676664]/40 hover:text-red-400" data-testid={`quick-time-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addTimeInput} className="inline-flex items-center gap-1 px-3 py-2 text-[11px] text-[#9A7B4F] border border-dashed border-[#9A7B4F]/40 hover:bg-[#9A7B4F]/5" data-testid="quick-time-add">
                  <Plus className="w-3 h-3" /> Add time
                </button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRangeGenerate} className="p-5 space-y-4" data-testid="range-form">
            <p className="text-[11px] text-[#676664]/60 leading-relaxed">
              Automatically opens back-to-back slots between <strong>Available from</strong> and <strong>Available until</strong>
              using the current Trial duration. Duplicates are skipped.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">From</label>
                <input type="date" required min={todayISO()} value={rFrom} onChange={e => setRFrom(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="range-from-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">To</label>
                <input type="date" required min={rFrom} value={rTo} onChange={e => setRTo(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="range-to-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Available from</label>
                <input type="time" required value={rStart} onChange={e => setRStart(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="range-window-start" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Available until</label>
                <input type="time" required value={rEnd} onChange={e => setREnd(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="range-window-end" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-2">Weekdays</label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_OPTIONS.map(w => {
                  const on = rWeekdays.includes(w.idx);
                  return (
                    <button type="button" key={w.idx} onClick={() => toggleWeekday(w.idx)}
                      className={`px-3 py-1.5 text-[11px] border transition-colors ${on ? "bg-[#9A7B4F] border-[#9A7B4F] text-white" : "bg-white border-[#282828]/15 text-[#282828] hover:border-[#9A7B4F]"}`}
                      data-testid={`range-weekday-${w.short.toLowerCase()}`}
                    >
                      {w.short}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#676664]/40 mt-1">Slots are only created on the selected weekdays within the date range.</p>
            </div>
            <Button type="submit" disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="range-generate-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Slots"}
            </Button>
          </form>
        )}
      </div>

      {/* Range filter + bulk toolbar */}
      <div className="flex flex-wrap items-end gap-3 mb-3" data-testid="filter-toolbar">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Showing from</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            className="border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="filter-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            className="border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="filter-to" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="text-[11px]" data-testid="refresh-slots-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </Button>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
          <button type="button" onClick={selectAllVisible} disabled={eligibleIds.size === 0}
            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] border border-[#282828]/12 text-[#282828] hover:bg-[#FAF8F0] disabled:opacity-30"
            data-testid="bulk-select-all">
            {allVisibleSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {allVisibleSelected ? "Clear all" : `Select all (${eligibleIds.size})`}
          </button>
          <span className="text-[11px] text-[#676664]/60" data-testid="bulk-count">
            {selectedIds.size} selected
          </span>
          <div className="flex gap-2 w-full sm:w-auto">
            <button type="button" onClick={() => runBulk("block")} disabled={selectedIds.size === 0 || bulkSubmitting}
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] border border-[#282828]/12 text-[#282828] hover:bg-[#FAF8F0] disabled:opacity-30 flex-1 sm:flex-none justify-center"
              data-testid="bulk-block-btn">
              <Lock className="w-3.5 h-3.5" /> Block
            </button>
            <button type="button" onClick={() => runBulk("unblock")} disabled={selectedIds.size === 0 || bulkSubmitting}
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] border border-[#282828]/12 text-[#282828] hover:bg-[#FAF8F0] disabled:opacity-30 flex-1 sm:flex-none justify-center"
              data-testid="bulk-unblock-btn">
              <Unlock className="w-3.5 h-3.5" /> Reopen
            </button>
            <button type="button" onClick={() => runBulk("delete")} disabled={selectedIds.size === 0 || bulkSubmitting}
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-30 flex-1 sm:flex-none justify-center"
              data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Slot list grouped by date */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
      ) : dateKeys.length === 0 ? (
        <div className="text-center py-16 text-[#676664]/50 text-[13px] bg-white border border-[#282828]/8" data-testid="empty-slots">
          No trial slots in this range. Use the form above to open new slots.
        </div>
      ) : (
        <div className="space-y-4">
          {dateKeys.map(date => {
            const daySlots = grouped[date].sort((a, b) => a.start_time.localeCompare(b.start_time));
            const dayEligible = daySlots.filter(s => s.status !== "booked");
            const daySelected = dayEligible.length > 0 && dayEligible.every(s => selectedIds.has(s.id));
            const label = new Date(date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
            return (
              <div key={date} className="bg-white border border-[#282828]/8" data-testid={`day-group-${date}`}>
                <div className="px-4 py-2 border-b border-[#282828]/5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => selectDay(date)} disabled={dayEligible.length === 0}
                      className="inline-flex items-center gap-1.5 text-[11px] text-[#676664] hover:text-[#9A7B4F] disabled:opacity-30"
                      data-testid={`day-select-${date}`}
                      aria-label={daySelected ? `Unselect ${date}` : `Select all ${date}`}
                    >
                      {daySelected ? <CheckSquare className="w-3.5 h-3.5 text-[#9A7B4F]" /> : <Square className="w-3.5 h-3.5" />}
                      <h4 className="text-[13px] text-[#282828] font-medium">{label}</h4>
                    </button>
                  </div>
                  <span className="text-[10px] text-[#676664]/50">{daySlots.length} slot{daySlots.length === 1 ? "" : "s"}</span>
                </div>
                <div className="divide-y divide-[#282828]/5">
                  {daySlots.map(slot => {
                    const checked = selectedIds.has(slot.id);
                    const bookable = slot.status !== "booked";
                    return (
                      <div key={slot.id} className={`px-3 sm:px-4 py-2.5 ${checked ? "bg-[#FAF8F0]" : ""}`} data-testid={`slot-row-${slot.id}`}>
                        {/* Desktop layout (md+) */}
                        <div className="hidden md:flex items-center gap-3">
                          <button type="button" onClick={() => toggleSelect(slot)} disabled={!bookable}
                            className="text-[#676664]/40 hover:text-[#9A7B4F] disabled:cursor-not-allowed disabled:opacity-30"
                            aria-label={checked ? `Unselect ${slot.start_time}` : `Select ${slot.start_time}`}
                            data-testid={`slot-check-${slot.id}`}
                          >
                            {checked ? <CheckSquare className="w-4 h-4 text-[#9A7B4F]" /> : <Square className="w-4 h-4" />}
                          </button>
                          <span className="text-[13px] text-[#282828] w-32 font-mono">
                            {slot.start_time} – {slot.end_time}
                          </span>
                          <StatusBadge status={slot.status} />
                          <span className="text-[11px] text-[#676664]/40 flex-1 truncate">
                            {slot.duration_minutes}m
                            {slot.status === "booked" && slot.customer_id ? ` · customer ${slot.customer_id.slice(0, 12)}` : ""}
                          </span>
                          <div className="flex items-center gap-2">
                            {bookable && (
                              <button onClick={() => toggleBlock(slot)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-[#282828]/12 text-[#282828] hover:bg-[#FAF8F0]"
                                data-testid={`slot-toggle-block-${slot.id}`}>
                                {slot.status === "blocked" ? (<><Unlock className="w-3 h-3" /> Unblock</>) : (<><Lock className="w-3 h-3" /> Block</>)}
                              </button>
                            )}
                            <button onClick={() => deleteSlot(slot)} disabled={!bookable}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`slot-delete-${slot.id}`}
                              title={bookable ? "Delete slot" : "Cancel the trial appointment to remove this slot"}>
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </div>
                        {/* Mobile card layout (<md) */}
                        <div className="md:hidden">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => toggleSelect(slot)} disabled={!bookable}
                              className="p-1.5 -m-1.5 text-[#676664]/40 hover:text-[#9A7B4F] disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label={checked ? "Unselect" : "Select"}
                              data-testid={`slot-check-mobile-${slot.id}`}
                            >
                              {checked ? <CheckSquare className="w-5 h-5 text-[#9A7B4F]" /> : <Square className="w-5 h-5" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] text-[#282828] font-mono">
                                {slot.start_time} – {slot.end_time}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <StatusBadge status={slot.status} />
                                <span className="text-[10px] text-[#676664]/50">{slot.duration_minutes}m</span>
                              </div>
                              {slot.status === "booked" && slot.customer_id && (
                                <div className="text-[10px] text-[#676664]/60 mt-1 font-mono truncate">customer {slot.customer_id.slice(0, 16)}…</div>
                              )}
                            </div>
                            <SlotActionMenu slot={slot} bookable={bookable} onToggleBlock={toggleBlock} onDelete={deleteSlot} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Section 19 — Mobile sticky FAB scrolls to the Create form */}
      <button
        type="button"
        onClick={() => {
          const el = document.getElementById("create-slots-form");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="md:hidden fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#9A7B4F] text-white shadow-lg flex items-center justify-center active:scale-95"
        style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        data-testid="mobile-add-slot-fab"
        aria-label="Add trial slot"
      >
        <Plus className="w-6 h-6" strokeWidth={2} />
      </button>
    </AdminLayout>
  );
}

// ─── Section 19 — mobile-only action menu (Block/Unblock/Delete) ─────────
function SlotActionMenu({ slot, bookable, onToggleBlock, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="p-2 -m-2 text-[#676664]"
        aria-label="Slot actions"
        data-testid={`slot-actions-mobile-${slot.id}`}
      >
        <MoreVertical className="w-5 h-5" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#282828]/12 shadow-lg min-w-[170px] py-1">
          {bookable && (
            <button
              onClick={() => { setOpen(false); onToggleBlock(slot); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-[#282828] hover:bg-[#FAF8F0]"
              data-testid={`slot-toggle-block-mobile-${slot.id}`}
            >
              {slot.status === "blocked" ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              {slot.status === "blocked" ? "Unblock" : "Block"}
            </button>
          )}
          <button
            onClick={() => { setOpen(false); onDelete(slot); }}
            disabled={!bookable}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`slot-delete-mobile-${slot.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
